<template>
    <fees-find
        :params="{ fee: $route.params.fee }"
        :include="[
            'alerts',
            'charges(paginate:1,3|include:transfer)',
            'tenancy(include:property(include:address))',
            'tasks(paginate:1,5)'
        ]"
    >
        <div
            slot-scope="{
                sid,
                data: fee,
                error,
                is_loading,
                has_loaded,
                find
            }"
        >
            <template v-if="!has_loaded || error">
                <base-loading
                    :error="error"
                    :retry="find"
                    class="py-6"
                />

                <error-modal
                    v-if="error"
                    :error="error"
                />
            </template>

            <template v-else>
                <base-alert
                    v-if="fee.archived_at"
                    level="warning"
                    class="mb-6"
                >
                    <template slot="heading">Fee Archived</template>

                    <template slot="content">
                        <p>This fee was archived on {{ fee.archived_at | moment('D MMM, YYYY') }}.</p>
                    </template>
                </base-alert>

                <details-card class="mb-6">
                    <template slot="header">
                        <div class="flex-grow">
                            <h1 class="text-lg font-normal">
                                {{ fee.due_at | moment('MMMM') }} Fee
                            </h1>
                        </div>

                        <div
                            v-if="is_loading"
                            class="w-6 h-6 mr-3"
                        >
                            <base-svg
                                name="icon-loading"
                                class="w-full h-full"
                            />
                        </div>

                        <edit-dropdown
                            v-if="$gate.role('landlord') && !fee.archived_at"
                            :fee="fee"
                            class="flex-none"
                            @success="find"
                        />
                    </template>

                    <template slot="body">
                        <grid-layout>
                            <grid-layout-item class="sm:w-1/2 lg:w-1/3 mt-3">
                                <div class="text-label text-xs">Due</div>
                                <div class="text-sm">{{ fee.due_at | moment('D MMM YYYY') }}</div>
                            </grid-layout-item>

                            <grid-layout-item class="sm:w-1/2 lg:w-1/3 mt-3">
                                <div class="text-label text-xs">Property</div>
                                <div class="text-sm">
                                    <template v-if="$gate.role('landlord')">
                                        <router-link
                                            :to="{
                                                name: $router.fix('dashboard.*.properties.show'),
                                                params: {
                                                    property: fee.tenancy.property.uuid
                                                }
                                            }"
                                            class="no-underline text-blue"
                                        >
                                            {{ fee.tenancy.property.name }}
                                        </router-link>
                                    </template>
                                    <template v-else>
                                            {{ fee.tenancy.property.address.name }}
                                    </template>
                                </div>
                            </grid-layout-item>

                            <grid-layout-item class="sm:w-1/2 lg:w-1/3 mt-3">
                                <div class="text-label text-xs">Tenancy</div>
                                <div class="text-sm">
                                    <router-link
                                        :to="{
                                            name: $router.fix('dashboard.*.tenancies.show'),
                                            params: {
                                                tenancy: fee.tenancy.uuid
                                            }
                                        }"
                                        class="text-blue no-underline"
                                    >
                                        {{ fee.tenancy.starts_at | moment('D MMM YYYY') }}
                                        <template v-if="fee.tenancy.ends_at">- {{ fee.tenancy.starts_at | moment('D MMM YYYY') }}</template>
                                        <template v-else>+</template>
                                    </router-link>
                                </div>
                            </grid-layout-item>

                            <grid-layout-item class="mt-3">
                                <div class="text-label text-xs">Description</div>
                                <div class="text-sm">{{ fee.description || 'n/a' }}</div>
                            </grid-layout-item>
                        </grid-layout>
                    </template>
                </details-card>

                <grid-layout>
                    <grid-layout-item class="mb-6 md:w-1/3">
                        <div class="flex flex-col bg-white h-full rounded shadow">
                            <header class="flex-none flex items-center h-16 px-4 sm:px-6 border-b">
                                <h3 class="flex-grow text-base font-normal">Balance</h3>

                                <span
                                    :class="punctualStateCss(fee.punctual_state)"
                                    class="flex-none text-sm font-semibold px-3 py-1 rounded-full"
                                >
                                    {{ $t(`fee.punctual_state.${fee.punctual_state}`) }}
                                </span>
                            </header>

                            <div class="flex-grow flex items-center leading-none p-4 sm:p-6">
                                <div class="flex-none">
                                    <div class="leading-none pt-2">
                                        <div class="text-3xl font-semibold mb-3">{{ fee.payable | money(0) }}</div>

                                        <div
                                            v-if="$gate.role('landlord')"
                                            class="text-xs text-grey-darker"
                                        >
                                            {{ fee.credited | money(0) }}  paid to date
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <component
                                :is="fee.payable > 0 ? 'router-link' : 'div'"
                                :to="{ name: $router.fix('dashboard.*.fees.charges.create') }"
                                :class="fee.payable > 0 ? 'bg-blue text-white' : 'bg-grey-lighter text-grey-dark cursor-not-allowed'"
                                class="rounded-b no-underline block w-full text-center py-3"
                            >
                                <div class="inline-flex items-center">
                                    Make a payment

                                    <base-svg
                                        :class="fee.payable > 0 ? 'fill-primary-white' : 'fill-primary-grey-dark'"
                                        name="icon-cheveron-right"
                                        class="w-6 h-6"
                                    />
                                </div>
                            </component>
                        </div>
                    </grid-layout-item>

                    <grid-layout-item class="mb-6 md:w-2/3">
                        <charges-card :fee="fee.uuid" />
                    </grid-layout-item>
                </grid-layout>

                <tasks-card
                    :data="fee.tasks.data"
                    :meta="fee.tasks.meta"
                    :add-to="{
                        name: $router.fix('dashboard.*.tasks.create'),
                        query: {
                            fee: fee.uuid
                        }
                    }"
                    :more-to="{
                        name: $router.fix('dashboard.*.tasks.index'),
                        query: {
                            fee: fee.uuid
                        }
                    }"
                    class="mb-6"
                />
            </template>
        </div>
    </fees-find>
</template>

<script>
    import ChargesCard from '../../../../components/charges-card'
    import DetailsCard from '../../../../components/details-card'
    import EditDropdown from '../../landlords/fees/show/edit-dropdown'
    import FeesFind from '../../../../components/fees-find'
    import StateCssMixin from '../../../../mixins/state-css'
    import TasksCard from '../../../../components/tasks-card'

    export default {
        mixins: [
            StateCssMixin
        ],
        components: {
            ChargesCard,
            DetailsCard,
            EditDropdown,
            FeesFind,
            TasksCard
        }
    }
</script>
